'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import StoreIcon, { PicelMark } from './StoreIcon';
import { useLocale } from '../lib/i18n';
import { getStoredSession, onAuthStateChange, signOut, type AuthSession } from '../lib/supabase/client';
import AuthModal from './AuthModal';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, Globe, LogOut, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [session, setSession] = useState<AuthSession | null>(null);
  const pathname = usePathname();
  const { locale, setLocale, t } = useLocale();

  useEffect(() => {
    setSession(getStoredSession());
    const unsub = onAuthStateChange((next) => {
      setSession(next);
    });
    return unsub;
  }, []);

  const [confirmSignOutOpen, setConfirmSignOutOpen] = useState(false);

  const toggleLanguage = () => {
    setLocale(locale === 'en' ? 'vi' : 'en');
  };

  const handleSignOut = async () => {
    await signOut();
    toast.info(locale === 'vi' ? 'Đã đăng xuất tài khoản' : 'Signed out successfully');
  };

  return (
    <>
      <header className="relative z-[100] bg-[#0b131d]/90 backdrop-blur border-b border-border/40 w-full" onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}>
        <a href="#picel-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2.5 focus:left-5 focus:z-[200] focus:px-4 focus:py-2.5 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:font-semibold focus:shadow-lg">
          Skip to content
        </a>
        <div className="w-full min-h-[72px] md:min-h-[80px] px-4 md:px-8 py-3 flex items-center justify-between gap-6 md:gap-8">
          <Link href="/" className="inline-flex items-center gap-2.5 text-primary shrink-0" aria-label="Picel home">
            <PicelMark />
            <span className="text-white font-serif font-bold text-2xl md:text-3xl tracking-tight">Picel</span>
          </Link>

          <nav
            id="store-navigation"
            className={cn(
              "items-center gap-6 md:gap-8 text-sm font-medium",
              open ? "flex flex-col absolute top-full left-0 right-0 p-6 bg-[#0e1622] border-b border-border shadow-2xl z-50 animate-in slide-in-from-top-2 duration-200" : "hidden md:flex"
            )}
            aria-label="Main navigation"
            onClick={() => setOpen(false)}
          >
            <Link
              href="/#catalog"
              className={cn(
                "flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-2 md:py-4 relative whitespace-nowrap",
                pathname === '/' && "text-primary font-semibold"
              )}
              aria-current={pathname === '/' ? 'page' : undefined}
            >
              {t('nav.all_assets')}
              {pathname === '/' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary hidden md:block" />}
            </Link>
            <Link
              href="/#bundles"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-2 md:py-4 relative whitespace-nowrap"
            >
              {t('nav.pro_bundles')}
            </Link>
          </nav>

          <div className="flex items-center gap-2.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={`Current language: ${locale === 'vi' ? 'Tiếng Việt' : 'English'}`}
                  className="h-8 gap-1.5 px-2.5 text-xs font-bold tracking-wider bg-secondary/50 border-border hover:bg-secondary text-foreground rounded-md"
                >
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{locale.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="w-36 bg-card border-border p-1 shadow-2xl rounded-md">
                <DropdownMenuItem
                  onClick={() => setLocale('vi')}
                  className={cn(
                    "flex items-center justify-between cursor-pointer py-2 px-2.5 rounded-sm",
                    locale === 'vi' && "text-primary font-bold bg-primary/10"
                  )}
                >
                  <span>Tiếng Việt</span>
                  {locale === 'vi' && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLocale('en')}
                  className={cn(
                    "flex items-center justify-between cursor-pointer py-2 px-2.5 rounded-sm",
                    locale === 'en' && "text-primary font-bold bg-primary/10"
                  )}
                >
                  <span>English</span>
                  {locale === 'en' && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
              <Link href="/#sprite-search" aria-label="Search sprites">
                <StoreIcon name="search" />
              </Link>
            </Button>

            {session ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-2 px-2.5 bg-secondary/40 border-border text-foreground hover:bg-secondary hover:border-primary/40 rounded-md transition-colors"
                    >
                      <div className="w-5 h-5 rounded bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold">
                        {session.user.email.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-xs font-semibold text-primary max-w-[120px] truncate">
                        {session.user.email.split('@')[0]}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={8} className="w-56 bg-card border-border p-2 shadow-2xl rounded-md">
                    <DropdownMenuLabel className="font-normal px-2.5 pt-2 pb-1.5">
                      <div className="flex flex-col space-y-1">
                        <p className="text-xs font-semibold text-foreground leading-none">
                          {session.user.email.split('@')[0]}
                        </p>
                        <p className="text-[11px] text-muted-foreground leading-none truncate">
                          {session.user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/library" className="flex items-center gap-2.5 cursor-pointer py-2 px-2.5 rounded-sm">
                        <StoreIcon name="download" />
                        <span className="text-xs font-medium">{t('nav.my_library')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/orders" className="flex items-center gap-2.5 cursor-pointer py-2 px-2.5 rounded-sm">
                        <StoreIcon name="check" />
                        <span className="text-xs font-medium">{locale === 'vi' ? 'Đơn hàng của tôi' : 'My Orders'}</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setConfirmSignOutOpen(true)}
                      className="flex items-center gap-2.5 cursor-pointer py-2 px-2.5 rounded-sm text-destructive focus:text-destructive focus:bg-destructive/10"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold">{t('nav.sign_out')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <AlertDialog open={confirmSignOutOpen} onOpenChange={setConfirmSignOutOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {locale === 'vi' ? 'Đăng xuất tài khoản?' : 'Sign out?'}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {locale === 'vi'
                          ? 'Bạn có chắc chắn muốn đăng xuất khỏi tài khoản của mình không?'
                          : 'Are you sure you want to sign out of your account?'}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {locale === 'vi' ? 'Hủy' : 'Cancel'}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleSignOut}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {locale === 'vi' ? 'Đăng xuất' : 'Sign Out'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => setAuthOpen(true)}
                className="h-8 px-4 text-xs font-semibold shadow-sm"
              >
                {t('nav.sign_in')}
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="md:hidden h-8 w-8 text-muted-foreground"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="store-navigation"
              onClick={() => setOpen(!open)}
            >
              <StoreIcon name={open ? 'close' : 'menu'} />
            </Button>
          </div>
        </div>
      </header>

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={(s) => setSession(s)}
      />
    </>
  );
}
